// =============================================================================
// components/DemoMode.tsx — R2 Developer Preview 1 Demo Mode Toggle
// Fixed floating toggle — bottom-right corner when logged in
// Activates demo data: EPE projects, BINS AI summaries, GIS results
// =============================================================================
import { useState, useEffect, type JSX } from 'react';
import { PlayCircle, StopCircle, Loader2, CheckCircle2, X, Rocket, Sparkles } from 'lucide-react';

const API = (import.meta as any).env?.VITE_BACKEND_URL
  ?? (typeof process !== 'undefined' ? (process.env as any)?.REACT_APP_BACKEND_URL ?? '' : '');

const DEMO_STORAGE_KEY = 'r2_demo_mode_active';

export function DemoModeToggle(): JSX.Element {
  const [open, setOpen]           = useState(false);
  const [active, setActive]       = useState(() => localStorage.getItem(DEMO_STORAGE_KEY) === 'true');
  const [loading, setLoading]     = useState(false);
  const [status, setStatus]       = useState<any>(null);
  const [result, setResult]       = useState<any>(null);
  const [notification, setNotification] = useState('');

  // Load demo status on mount
  useEffect(() => {
    fetch(`${API}/api/r2-demo/status`).then(r => r.json()).then(setStatus).catch(() => {});
  }, []);

  const activate = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/r2-demo/activate`, { method: 'POST' }).then(r => r.json());
      setResult(r);
      setActive(true);
      localStorage.setItem(DEMO_STORAGE_KEY, 'true');
      setNotification('Demo Mode activated! Sample projects and data are ready.');
      setTimeout(() => setNotification(''), 5000);
      // Refresh status
      const s = await fetch(`${API}/api/r2-demo/status`).then(r => r.json());
      setStatus(s);
    } catch {
      setNotification('Failed to activate demo data. Please try again.');
      setTimeout(() => setNotification(''), 4000);
    }
    setLoading(false);
  };

  const deactivate = () => {
    setActive(false);
    localStorage.removeItem(DEMO_STORAGE_KEY);
    setResult(null);
    setNotification('Demo Mode deactivated.');
    setTimeout(() => setNotification(''), 3000);
  };

  return (
    <>
      {/* Notification Toast */}
      {notification && (
        <div data-testid="demo-notification"
          style={{ position: 'fixed', bottom: 80, right: 20, zIndex: 9990, background: 'rgba(6,14,34,0.95)', border: '1px solid rgba(45,212,191,0.4)', borderRadius: 10, padding: '10px 16px', fontSize: 12, color: '#f0f4ff', maxWidth: 300, lineHeight: 1.5, boxShadow: '0 4px 24px rgba(0,0,0,0.5)' }}>
          {notification}
        </div>
      )}

      {/* Floating Toggle Button */}
      <button data-testid="demo-mode-toggle"
        onClick={() => setOpen(v => !v)}
        style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 9980,
          background: active ? 'rgba(251,191,36,0.18)' : 'rgba(6,14,34,0.9)',
          border: `1.5px solid ${active ? 'rgba(251,191,36,0.5)' : 'rgba(45,212,191,0.3)'}`,
          borderRadius: 24, padding: '8px 16px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 7,
          color: active ? '#fbbf24' : '#5eead4',
          fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
          boxShadow: '0 2px 16px rgba(0,0,0,0.4)',
          transition: 'all 0.2s',
        }}>
        <Rocket size={12}/>
        {active ? 'Demo Active' : 'Demo Mode'}
        {active && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fbbf24', animation: 'pulse 2s infinite' }}/>}
      </button>

      {/* Demo Mode Panel */}
      {open && (
        <div style={{ position: 'fixed', bottom: 60, right: 20, zIndex: 9981, width: 320, background: 'rgba(6,14,34,0.98)', border: '1px solid rgba(30,55,100,0.5)', borderRadius: 14, overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }}>
          {/* Header */}
          <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(30,55,100,0.4)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Rocket size={14} style={{ color: '#fbbf24' }}/>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#f0f4ff' }}>Demo Mode</div>
              <div style={{ fontSize: 10, color: 'rgba(148,172,210,0.65)' }}>Developer Preview 1</div>
            </div>
            <button onClick={() => setOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(148,172,210,0.65)' }}><X size={14}/></button>
          </div>

          {/* Content */}
          <div style={{ padding: '14px 16px' }}>
            {/* Current status */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
              {[
                { label: 'Hospital EPE Project', ok: status?.hospital_project_seeded },
                { label: 'Data Centre EPE Project', ok: status?.datacenter_project_seeded },
                { label: 'BINS Tower (100 floors)', ok: status?.bins_ready },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: 'rgba(148,172,210,0.65)' }}>
                  <CheckCircle2 size={12} style={{ color: item.ok ? '#4ade80' : 'rgba(60,90,140,0.5)', flexShrink: 0 }}/>
                  {item.label}
                  <span style={{ marginLeft: 'auto', fontSize: 9.5, color: item.ok ? '#4ade80' : '#f87171', fontWeight: 700 }}>
                    {item.ok ? 'READY' : 'NOT SEEDED'}
                  </span>
                </div>
              ))}
            </div>

            {/* What demo mode does */}
            <div style={{ background: 'rgba(251,191,36,0.05)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 8, padding: '10px 12px', marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#fbbf24', textTransform: 'uppercase', marginBottom: 6 }}>What Demo Mode Activates</div>
              {[
                'Hospital EPE project (450 beds, pre-generated outputs)',
                'Data Centre EPE project (2.4 MW Tier III, pre-generated)',
                'BINS EPE links for ICU + OR rooms',
                'Realistic sample data — no manual entry needed',
              ].map(item => (
                <div key={item} style={{ fontSize: 11, color: 'rgba(148,172,210,0.65)', padding: '2px 0', display: 'flex', gap: 6 }}>
                  <Sparkles size={10} style={{ color: '#fbbf24', flexShrink: 0, marginTop: 1 }}/>{item}
                </div>
              ))}
            </div>

            {/* Actions */}
            {!active ? (
              <button data-testid="demo-activate-btn"
                onClick={activate} disabled={loading}
                style={{ width: '100%', background: loading ? 'rgba(30,50,90,0.4)' : '#fbbf24', color: loading ? 'rgba(148,172,210,0.65)' : 'rgb(6,12,26)', border: 'none', borderRadius: 8, padding: '10px 0', fontWeight: 800, fontSize: 12, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                {loading ? <><Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }}/> Seeding Demo Data…</> : <><PlayCircle size={13}/> Activate Demo Mode</>}
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.25)', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5, color: '#4ade80', fontWeight: 700 }}>
                  <CheckCircle2 size={12}/> Demo Mode Active
                </div>
                {result && (
                  <div style={{ fontSize: 11, color: 'rgba(148,172,210,0.65)' }}>
                    Hospital project: <code style={{ color: '#5eead4', fontSize: 10 }}>{result.hospital?.project_id?.slice(0, 12)}…</code><br/>
                    DC project: <code style={{ color: '#5eead4', fontSize: 10 }}>{result.data_center?.project_id?.slice(0, 12)}…</code>
                  </div>
                )}
                <button data-testid="demo-deactivate-btn"
                  onClick={deactivate}
                  style={{ background: 'transparent', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 8, padding: '8px 0', fontSize: 11, color: '#f87171', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <StopCircle size={11}/> Deactivate Demo
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse { 0%,100%{ opacity:1 } 50%{ opacity:0.4 } }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      `}</style>
    </>
  );
}